import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { getErpJwtToken, getErpUserId } from 'app/services/auth/erp-auth-token.util';

export interface AccesLogicielCaisseRow {
  id?: number;
  email: string;
  code_station: string;
  is_active?: boolean;
  name?: string;
  station_id?: number;
}

export interface CreateAccesLogicielCaissePayload {
  email: string;
  password: string;
  code_station: string;
  station_id?: number;
  name?: string;
  station_nom?: string;
  matricule_fiscale?: string;
  id_user?: number;
}

/** Double vérification ERP (acces_logiciel_caisse) + back caisse (users). */
export interface AccesSyncStatus {
  success?: boolean;
  code_station?: string;
  resolved_code_station?: string;
  sync_ok?: boolean;
  action?: 'setup' | 'configure' | 'resync';
  erp?: {
    has_acces: boolean;
    count: number;
    acces: AccesLogicielCaisseRow[];
  };
  back?: {
    checked: boolean;
    exists: boolean;
    has_station: boolean;
    email?: string | null;
    user_id?: number | null;
    station_id?: number | null;
    error?: string | null;
  };
  /** Identifiants ERP (mot de passe en clair) pour login logiciel caisse — si sync_ok. */
  credentials?: {
    email: string;
    code_station: string;
    password: string;
  } | null;
  password_legacy_hash?: boolean;
}

interface AccesListResponse {
  success?: boolean;
  data?: AccesLogicielCaisseRow[];
}

interface AccesCreateResponse {
  success?: boolean;
  data?: AccesLogicielCaisseRow;
  message?: string;
  logiciel_caisse?: Record<string, unknown>;
}

/** Réponse gateway ERP → entrepôts LC pour sélecteurs documents. */
export interface AccesEntrepotsForDocumentResponse {
  success?: boolean;
  message?: string;
  code_station?: string;
  resolved_code_station?: string;
  source?: string | null;
  lc?: { user_id?: number | null; station_id?: number | null } | null;
  entrepots?: Array<Record<string, unknown>>;
}

/**
 * Gateway → erp_backend client (/api/users/acces-logiciel-caisse).
 * Le client appelle le back caisse (check + ensure) côté serveur.
 */
@Injectable({ providedIn: 'root' })
export class AccesLogicielCaisseService {
  private readonly baseUrl = `${environment.apiUrl}api/users/acces-logiciel-caisse`;

  constructor(private readonly http: HttpClient) {}

  private authHeaders(): HttpHeaders {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const jwt = getErpJwtToken();
    if (jwt) {
      headers.Authorization = `Bearer ${jwt}`;
    }
    return new HttpHeaders(headers);
  }

  private userParams(): HttpParams {
    let params = new HttpParams();
    const erpUserId = getErpUserId();
    if (erpUserId) {
      params = params.set('id_user', String(erpUserId));
    }
    return params;
  }

  /** Vérifie acces_logiciel_caisse (ERP) + user back caisse. */
  getSyncStatus(code_station: string, erpStationId?: number): Observable<AccesSyncStatus> {
    const code = String(code_station || '').trim();
    let params = this.userParams();
    if (erpStationId != null && erpStationId > 0) {
      params = params.set('station_id', String(erpStationId));
    }
    try {
      const raw = localStorage.getItem('currentUser');
      const email = raw ? String(JSON.parse(raw)?.email || '').trim().toLowerCase() : '';
      if (email) {
        params = params.set('email', email);
      }
    } catch {
      /* ignore */
    }
    return this.http
      .get<AccesSyncStatus>(`${this.baseUrl}/sync-status/${encodeURIComponent(code)}`, {
        headers: this.authHeaders(),
        params,
      })
      .pipe(
        map((res): AccesSyncStatus => res ?? { action: 'setup', sync_ok: false }),
        catchError((err) =>
          throwError(() => new Error(this.readError(err, 'Impossible de vérifier la synchronisation caisse.')))
        )
      );
  }

  listByCodeStation(code_station: string): Observable<AccesLogicielCaisseRow[]> {
    const code = String(code_station || '').trim();
    let params = this.userParams().set('code_station', code);

    return this.http
      .get<AccesListResponse>(`${this.baseUrl}/station/${encodeURIComponent(code)}`, {
        headers: this.authHeaders(),
        params,
      })
      .pipe(
        map((res) => (res?.success && Array.isArray(res.data) ? res.data : [])),
        catchError((err) =>
          throwError(() => new Error(this.readError(err, 'Impossible de charger les accès logiciel caisse.')))
        )
      );
  }

  /**
   * GET /api/users/acces-logiciel-caisse/entrepots
   * Résout le couple LC (coordination / sync) puis renvoie les entrepôts caisse.
   */
  listEntrepotsForDocument(
    code_station: string,
    erpStationId?: number
  ): Observable<AccesEntrepotsForDocumentResponse> {
    const code = String(code_station || '').trim();
    let params = this.userParams();
    if (code) {
      params = params.set('code_station', code);
    }
    if (erpStationId != null && erpStationId > 0) {
      params = params.set('station_id', String(erpStationId));
    }

    return this.http
      .get<AccesEntrepotsForDocumentResponse>(`${this.baseUrl}/entrepots`, {
        headers: this.authHeaders(),
        params,
      })
      .pipe(
        map((res) => ({
          ...(res || {}),
          entrepots: Array.isArray(res?.entrepots) ? res.entrepots : [],
        })),
        catchError((err) =>
          throwError(
            () => new Error(this.readError(err, 'Impossible de charger les entrepôts logiciel caisse.'))
          )
        )
      );
  }

  create(payload: CreateAccesLogicielCaissePayload): Observable<AccesLogicielCaisseRow> {
    const erpUserId = getErpUserId();
    const body: CreateAccesLogicielCaissePayload = {
      ...payload,
      ...(erpUserId ? { id_user: erpUserId } : {}),
    };

    if (!body.id_user) {
      return throwError(() => new Error('Utilisateur ERP non identifié. Reconnectez-vous.'));
    }

    return this.http
      .post<AccesCreateResponse>(this.baseUrl, body, { headers: this.authHeaders() })
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.message || 'Création accès logiciel caisse échouée.');
          }
          return res.data;
        }),
        catchError((err) => {
          const status = (err as { status?: number })?.status;
          const message = this.readError(err, 'Création accès logiciel caisse échouée.');
          return throwError(() => {
            const error = new Error(message) as Error & { status?: number };
            error.status = status;
            return error;
          });
        })
      );
  }

  private readError(err: unknown, fallback: string): string {
    if (err && typeof err === 'object') {
      const e = err as {
        status?: number;
        statusText?: string;
        error?: {
          message?: string;
          error?: string;
          logiciel_caisse?: { error?: string; message?: string };
        };
        message?: string;
      };
      if (e.status === 0) {
        return 'requête bloquée (CORS ou réseau vers back-erp)';
      }
      const fromCaisse =
        e.error?.logiciel_caisse?.error || e.error?.logiciel_caisse?.message;
      if (fromCaisse || e.error?.message || e.error?.error || e.message) {
        return fromCaisse || e.error?.message || e.error?.error || e.message || fallback;
      }
      if (e.status) {
        return `HTTP ${e.status}${e.statusText ? ` ${e.statusText}` : ''}`;
      }
    }
    return fallback;
  }
}

